/* eslint-disable complexity */
/**
 * 文件消息组件
 * 使用方：用户对话，群组对话
 * @author sunzhiguang
 * @date 2020/6/10
 */

import React, { useCallback, useContext, useEffect, useState, useMemo } from "react";
import "./index.less";
import IconFont from "@/components/icon";
import { message as toast, Dropdown } from "antd";
import { DownOutlined } from "@/utils/antdIcon";
import { useTranslation, WithTranslation } from "react-i18next";
import {
  ChatMessage,
  ChatMessageType,
  FileChatMessage,
  FileStatus,
  MessageStatus
} from "@/types/chat";
import {
  downLoadFile,
  getFileIconByMime,
  getFileName,
  getMessageFileName,
  getFileUrl,
  sizeToStr,
  pushAutoDownload,
  getPreviewCanEdit,
  isImageFileName
} from "@/utils/chat/index";
import { MessageSendType } from "@/types/chat/enum";
import { connect } from "dva";
import ChatState from "@/types/chat/State";
import { lookup } from "mime-types";
import { DownloadItem, FocusSDK, isFocusEnv, detectOS } from "@/utils";
import ImService from "@/server/ImService";
import ConnectState from "@jd/jdee.im.sdk/lib/es/enum/ConnectState";
import {
  changeEgovUrl,
  crossAppSwitch,
  getFileOperate,
  getFileOperateAuthority,
  getFileTagById,
  getFileTagName,
  KEY_CONVERT
} from "@/utils/tools";
import GlobalContext, { GlobalContextConfig } from "@/context/GlobalContext";
import { SaveOption } from "@/utils/DownloadFileQueue";
import {
  handleAddFileDownloadRecord,
  checkFileIsExpired,
  getPreviewImageModal
} from "@/components/chat/utils/commonMessageModal";
import DownloadService from "@/server/DownloadService";
import {
  getFileDownloadBzOptions,
  isCrossPlatformGroupSession,
  isEqualEmployee,
  isOuterContacts,
  parseMessageToUserBySingle,
  showIntentTail
} from "@/utils/chat/message";
import ChatContext from "@/components/chat/context";
import { getNetCheckUrl } from "@/config/config";
import NetworkCheck, { UseCheckType } from "@/utils/NetworkCheck";
import i18n from "@/i18n";
import { getGroupMemberList, getWpsPreviewCode } from "@/api/chat";
import { groupCreates } from "@/components/Joyspace/api";
import { handleTransferTo } from "@/components/chat/message/Item/utils";
import { UserModel } from "@/models/user";
import { canJoyspacePreview, openJoyspacePreview } from "@/components/Joyspace/util";
import { addXclient } from "@/utils/addToken";
import egovFileIcon from "@/assets/images/im/egov-file-icon.png";
import UploadWithDetailBtn from "./UploadWithDetailBtn";
import { FileTipTag } from "@/components/Tags";
import AIIntentTail from "../../AIIntentTail";
import { FILE_DOWNLOAD_BIZTYPE } from "@/utils/chat/message";
import { pick } from "lodash";
import MessageDongReadStatusBtn from "@/components/chat/message/Item/MessageDongReadStatusBtn";
/**
 *
  "click-to-save", // 下载
  "click-to-saveAs", // 另存为
  "click-to-saveTo" // 转存到
 */

interface DownLoadFileProps {
  message: FileChatMessage;
  sendType: MessageSendType;
  isMergeForward?: boolean;
  originMessage?: any;
  updateSessionMessageMid: Function;
  updateSessionMessagesTimestamp: Function;
  downloadingMids: string[];
  updateDownloadingMids: Function;
  saveAsCount: number;
  userData: UserModel;
  pushChatMessageMid: (data: { sessionId: string; message: Partial<ChatMessage> }) => void;
}

type IDownLoadFileProps = Readonly<WithTranslation & DownLoadFileProps & ChatState>;

let dXhr: any = {};
const STATUS_SAVE_TIME = 1000 * 60 * 60 * 30;
// eslint-disable-next-line complexity
function DownLoadFile(props: IDownLoadFileProps) {
  const {
    message,
    connectState,
    currentEmployee,
    isMergeForward,
    originMessage,
    updateSessionMessageMid,
    updateSessionMessagesTimestamp,
    selectedSession,
    sendType,
    downloadingMids,
    updateDownloadingMids,
    saveAsCount,
    userData,
    pushChatMessageMid
  } = props;
  const { t } = useTranslation("chat");
  const { isSmall, isMeetingChat, fileTagList, currentFileControl } = React.useContext(ChatContext);
  const downloadServiceInstance = DownloadService.getInstance({
    updateDownloadingMids
  } as any);
  const msgId = isMergeForward
    ? `${(message as any).muuid}${originMessage.mid}`
    : (message as any).mid;
  const downloadStatus = downloadServiceInstance.getDownloadStatus(msgId);
  const [status, setStatus] = useState(downloadStatus);
  const [path, setPath] = useState(message.downLocalPath || "");
  const [isFileExpired, setIsFileExpired] = useState(false);
  const [progress, setProgress] = useState(0);
  const [downloadItem, setDownloadItem]: any = useState({});
  const [downloadXhr, setDownloadXhr]: any = useState({});
  const [isDragging, setIsDragging] = useState(false);
  const [isWpsFile, setIsWpsFile] = useState(false);
  const { groupRosters } = React.useContext(ChatContext);
  const { appRuntimeEnv, localConfigData } = useContext<GlobalContextConfig>(GlobalContext);
  const imDownloadSaveAs = localConfigData?.settings?.imDownloadSaveAs;
  const {
    joyspaceHost,
    im,
    joyspaceWpsTarget,
    fileService: { imAppKey }
  } = appRuntimeEnv;
  const defaultDownloadPath =
    appRuntimeEnv.registryConfig.defaultDownloadPath || FocusSDK.getDownloadPath();
  const isOutApp = selectedSession.isGroup
    ? isCrossPlatformGroupSession(selectedSession)
    : isOuterContacts(selectedSession, currentEmployee);
  const isDisplay = !isOutApp && crossAppSwitch("joyspace");

  const getFileConfig = (status: FileStatus): string[] => {
    const data: any = {
      [FileStatus.INIT]: isFocusEnv()
        ? getFileOperate([
              "click-to-save", // 下载
              "click-to-saveAs", // 另存为
              "click-to-saveTo" // 转存到
            ],
            currentFileControl || [],
            message.fileTag || 0
          )
        : getFileOperate(
            ["click-to-save", "", "click-to-saveTo"],
            currentFileControl || [],
            message.fileTag || 0
          ),
      [FileStatus.DOWNLOADING]: getFileOperate(
        ["cancel-download", "", "click-to-saveTo"],
        currentFileControl || [],
        message.fileTag || 0
      ),
      [FileStatus.CANCELED]: getFileOperate(
        ["download-resume", "click-to-saveAs", "click-to-saveTo"],
        currentFileControl || [],
        message.fileTag || 0
      ),
      [FileStatus.FAILED]: getFileOperate(
        ["re-download", "click-to-saveAs", "click-to-saveTo"],
        currentFileControl || [],
        message.fileTag || 0
      ),
      [FileStatus.NOTHAVE]: getFileOperate(
        ["re-download", "click-to-saveAs", "click-to-saveTo"],
        currentFileControl || [],
        message.fileTag || 0
      ),
      [FileStatus.FAILEDRETRY]: getFileOperate(
        ["download-resume", "click-to-saveAs", "click-to-saveTo"],
        currentFileControl || [],
        message.fileTag || 0
      ),
      [FileStatus.COMPLETED]: isFocusEnv()
        ? getFileOperate(["open-text", "", "click-to-saveTo"],
          currentFileControl || [],
            message.fileTag || 0
          )
        : getFileOperate(["click-to-save", "", "click-to-saveTo"],
            currentFileControl || [],
            message.fileTag || 0
          )
    };
    return data[status];
  };
  const [operateKey1, operateKey2, operateKey3] = getFileConfig(status as FileStatus);

  const isEgovFile = useMemo(() => {
    return message.type === ChatMessageType.EGOV_FILE || message.kind === ChatMessageType.EGOV_FILE;
  }, [message]);

  const isOperateNoAuth = useMemo(() => {
    return !operateKey1 && !operateKey2 && !operateKey3;
  }, [operateKey1, operateKey2, operateKey3]);

  const getDownloadOptions = ({ viewFile, saveAs, message, auto, isGroup }: SaveOption) => {
    const saveName = getMessageFileName(message);
    const needSend = !auto;
    return {
      onStarted: (downloadItem: DownloadItem) => {
        setStatus(FileStatus.DOWNLOADING);
        setDownloadItem(downloadItem);
      },
      onProgress: ({ percent = 0 }) => {
        setStatus(FileStatus.DOWNLOADING);
        setProgress(percent);
      },
      onCancel: (alreadySaveAs?: boolean) => {
        if (!alreadySaveAs && message.downLocalPath) {
          setStatus(FileStatus.NOTHAVE);
        } else {
          setStatus(status);
        }
      },
      onPause: () => {
        setStatus(FileStatus.CANCELED);
      },
      onResume: () => {
        setStatus(FileStatus.DOWNLOADING);
      },
      onFinish: async (file: { path: string; filename: string }, data: any) => {
        console.log("file=>", file);
        // 当多个文件同时下载时，file.path 获取路径错误
        const path = file.path;
        // console.log(detectOS());
        // const path = `${data.saveAs}${detectOS() === 'Win' ? '\\' : '/'}${file.filename}`;
        // console.log('path=>', data.saveAs, path);
        // // tj: 解决保存为头像地址
        // if (!checkDownloadFilePathValid(saveName, file)) {
        //     setStatus(FileStatus.INIT);
        //     if (!auto) {
        //         toast.error(t('failed'));
        //     }
        //     setIsDragging(false);
        //     return;
        // }
        if (isMergeForward) {
          setStatus(FileStatus.COMPLETED);
          setProgress(100);
          setPath(path);
          downloadServiceInstance.setDownloadStatus(
            `${(message as any).muuid}${originMessage.mid}`,
            FileStatus.COMPLETED
          );
          // if (viewFile) {
          //     FocusSDK.checkLocalPathExist(path, (exists: boolean) => {
          //         if (exists) {
          //             setTimeout(() => {
          //                 FocusSDK.openItem(path);
          //             }, 500);
          //         } else {
          //             toast.error('文件下载失败');
          //         }
          //     });
          // }
          setIsDragging(false);
          if (saveAs) {
            localStorage.setItem("downloadFilePath", data.saveAs);
          }
          return;
        }
        const imService = ImService.getInstance();
        const result = await imService.updateLocalPath(
          (message as FileChatMessage).sessionId,
          (message as FileChatMessage).mid,
          {
            downLocalPath: path
          }
        );
        console.log("result=>", result);
        if (result) {
          console.log("status=>", status, message);
          setStatus(FileStatus.COMPLETED);
          setProgress(100);
          setPath(path);
          downloadServiceInstance.setDownloadStatus((message as any).mid, FileStatus.COMPLETED);
          console.log(
            "getdownloadStatues=>",
            downloadServiceInstance.getDownloadStatus((message as any).mid)
          );
          await updateSessionMessageMid({
            sessionId: (message as FileChatMessage).sessionId,
            sendResult: { body: { ...message, downLocalPath: path } }
          });
          // if (viewFile) {
          //     FocusSDK.checkLocalPathExist(path, (exists: boolean) => {
          //         if (exists) {
          //             setTimeout(() => {
          //             }, 500);
          //         } else {
          //             toast.error('文件下载失败');
          //         }
          //     });
          // }
          setIsDragging(false);
          if (saveAs) {
            localStorage.setItem("downloadFilePath", data.saveAs);
          }
          // toast.success('下载完成');
        } else {
          if (!auto) {
            toast.error(t("failed"));
          }
          setIsDragging(false);
        }
        // 下载完成提交下载数据
        if (needSend && !isEqualEmployee((message as FileChatMessage).sender, currentEmployee)) {
          handleAddFileDownloadRecord(
            message as FileChatMessage,
            currentEmployee,
            0,
            selectedSession
          );
        }
      },
      onError: (error: any) => {
        console.log("error=>", error);
        setStatus(isRetryFn() ? FileStatus.FAILEDRETRY : FileStatus.FAILED);
        if (error.errorCode === 1017005) {
          const imService = ImService.getInstance();
          imService.updateMessage(message.sessionId, message.mid, {
            statusType: MessageStatus.EXPIRED
          });
          updateSessionMessageMid({
            sessionId: message.sessionId,
            sendResult: { body: { ...message, statusType: MessageStatus.EXPIRED } }
          });
          setIsFileExpired(true);
        }
      }
    };
  };

  const isImageFile = useMemo(() => {
    const isImg = isImageFileName(getFileName(message));
    return isImg;
  }, [message]);

  const canDownload = useMemo(() => {
    const auth = getFileOperateAuthority("download", currentFileControl || [], message?.fileTag || 0);
    return auth ? 1 : 0;
  }, [currentFileControl, message.fileTag]);

  const canJoyspaceEdit = useMemo(() => {
    const auth = getFileOperateAuthority("tranferTo", currentFileControl || [], message?.fileTag || 0);
    return auth ? 1 : 0;
  }, [currentFileControl, message.fileTag]);

  const hasAuthPreview = useMemo(() => {
    const auth = getFileOperateAuthority(
      "onlinePreview",
      currentFileControl || [],
      message?.fileTag || 0
    );
    return auth;
  }, [currentFileControl, message.fileTag]);

  const canWpsPreview = useMemo(() => {
    return hasAuthPreview && isDisplay && !selectedSession.isSecret && joyspaceWpsTarget === 'private';
  }, [hasAuthPreview, isDisplay, selectedSession.isSecret]);

  useEffect(() => {
    if (message.name) {
      const res = canJoyspacePreview(message.name);
      setIsWpsFile(res);
    }
  }, [message?.name]);

  const doDownloadMessageFile = async ({
    viewFile,
    saveAs,
    message,
    auto = false,
    isGroup
  }: SaveOption): Promise<void> => {
    // if (message.type === ChatMessageType.EGOV_FILE) {
    //   message.url = await addXclient(message.url);
    // }
    const options = getDownloadOptions({ viewFile, saveAs, message, auto, isGroup });
    return downloadServiceInstance.download(
      { viewFile, saveAs, message, auto, isGroup, isMergeForward, originMessage },
      options
    );
    // return downloadMessageFile({ viewFile, saveAs, message, auto }, options);
  };

  const isRetryFn = () => {
    const uniqueId = isMergeForward
      ? `${(message as any).muuid}${originMessage.mid}`
      : (message as any).mid;
    const downloadRetryTime = downloadServiceInstance.getDownloadRetryTime(uniqueId);
    let isRetry = false;
    let pastTime;
    if (downloadRetryTime) {
      pastTime = new Date().getTime() - downloadRetryTime;
      isRetry = pastTime < STATUS_SAVE_TIME;
    }
    if (pastTime) {
      const diffTime = STATUS_SAVE_TIME - pastTime;
      setTimeout(() => {
        clearItem(uniqueId);
      }, diffTime);
    }
    return isRetry;
  };

  const clearItem = (uniqueId: any) => {
    const saveOption = downloadServiceInstance.getSaveOption(uniqueId);
    if (saveOption) {
      const options = getDownloadOptions(saveOption);
      const downloadItem = downloadServiceInstance.getDLServiceItem(uniqueId, options);
      FocusSDK.dlCbsClear(downloadItem?.cid);
      downloadItem?.cancel();
    }
    downloadServiceInstance.clearServiceItem(uniqueId);
  };

  const fileExpired = useMemo(() => {
    return message.statusType === MessageStatus.EXPIRED || isFileExpired;
  }, [message.statusType, isFileExpired]);

  useEffect(() => {
    if (isFocusEnv()) {
      console.log("downloadingMids=>", downloadingMids);
      // console.log(message, "message");
      // 优先判断该文件是否在下载中
      const uniqueId = isMergeForward
        ? `${(message as any).muuid}${originMessage.mid}`
        : (message as any).mid;
      if (downloadingMids && downloadingMids.length && downloadingMids.includes(uniqueId)) {
        const saveOption = downloadServiceInstance.getSaveOption(uniqueId);
        if (saveOption) {
          const options = getDownloadOptions(saveOption);
          const downloadItem = downloadServiceInstance.getDLServiceItem(uniqueId, options);
          setDownloadItem(downloadItem);
          const tempStatus = downloadServiceInstance.getDownloadStatus(uniqueId);
          if (isRetryFn() && tempStatus === FileStatus.FAILED) {
            setStatus(FileStatus.FAILEDRETRY);
          } else {
            setStatus(tempStatus);
          }
        }
      } else {
        const downLocalPath = path;
        const _status = downloadServiceInstance.getDownloadStatus(uniqueId);
        if (_status === FileStatus.FAILED) {
          if (isRetryFn()) {
            clearItem(uniqueId);
          }
          setStatus(_status);
          setPath("");
        } else if (downLocalPath) {
          FocusSDK.checkLocalPathExist(downLocalPath, (exists: boolean) => {
            if (exists) {
              setStatus(FileStatus.COMPLETED);
              setPath(downLocalPath);
              downloadServiceInstance.setDownloadStatus(uniqueId, FileStatus.COMPLETED);
            } else {
              downloadServiceInstance.setDownloadStatus(uniqueId, FileStatus.NOTHAVE);
              setStatus(downloadServiceInstance.getDownloadStatus(uniqueId));
              setPath("");
            }
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadingMids, message.mid, path, saveAsCount]);

  useEffect(() => {
    if (message.downLocalPath) {
      setPath(message.downLocalPath);
    }
  }, [message.downLocalPath]);

  useEffect(() => {
    if (
      !selectedSession?.isSecret &&
      !isMergeForward &&
      (!isEgovFile || (FocusSDK.isPrivateNet() && message.type === ChatMessageType.EGOV_FILE)) &&
      message.statusType !== MessageStatus.EXPIRED
    ) {
      const isGroup = selectedSession.isGroup;
      if (!downloadingMids?.includes((message as any).mid) && canDownload) {
        pushAutoDownload(message, currentEmployee, (options: any) =>
          doDownloadMessageFile({
            ...options,
            isGroup
          })
        );
      }
    }
    // 切出聊天页，组件销毁时，如果下载未完成，自动终止下载
    return () => {
      dXhr.abort?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canDownload]);

  useEffect(() => {
    if (status !== FileStatus.COMPLETED) {
      return;
    }
    const needManualSet =
      selectedSession?.isSecret &&
      sendType === MessageSendType.OTHER &&
      message?.expire &&
      !message.readTimeOfMessage;
    if (!needManualSet) {
      return;
    }
    const readTimeOfMessage = new Date().getTime();
    const imService = ImService.getInstance();
    imService
      .updateReadTime(message.sessionId, message.mid, {
        ...message,
        readTimeOfMessage
      })
      .then(async (result) => {
        if (result) {
          await updateSessionMessageMid({
            sessionId: message.sessionId,
            sendResult: {
              body: { ...message, readTimeOfMessage, downLocalPath: path }
            }
          });
          await updateSessionMessagesTimestamp({
            timestamp: new Date().getTime()
          });
        }
      });
  }, [status]); // eslint-disable-line

  // 设置消息读取时间
  const handleSetReadTime = async () => {
    const needManualSet =
      selectedSession?.isSecret &&
      sendType === MessageSendType.OTHER &&
      message?.expire &&
      !message.readTimeOfMessage;
    if (!needManualSet) {
      return;
    }
    const readTimeOfMessage = new Date().getTime();
    const imService = ImService.getInstance();
    const result = await imService.updateReadTime(message.sessionId, message.mid, {
      ...message,
      readTimeOfMessage
    });
    if (result) {
      await updateSessionMessageMid({
        sessionId: message.sessionId,
        sendResult: {
          body: { ...message, readTimeOfMessage, downLocalPath: path }
        }
      });
      await updateSessionMessagesTimestamp({
        timestamp: new Date().getTime()
      });
    }
  };

  const fileOperateClsTag = (operateTipKey: string): string | undefined => {
    if (!operateTipKey) {
      return undefined;
    }
    const preStr = "pageclick|keycount|Xtbg_Msg_Info_File";
    return `${preStr}|${operateTipKey}`;
  };

  const netServer = NetworkCheck.getInstance({
    url: getNetCheckUrl(),
    checkType: UseCheckType.API
  });
  const groupCreate = useCallback(async () => {
    const { name } = selectedSession.info as any;
    const owner = (selectedSession as any)?.info || {};
    const onLine = await netServer.checkNet();
    if (!onLine) {
      toast.error(i18n.t("Network Error"));
      return;
    }
    let groupList = groupRosters;
    try {
      if (Array.isArray(groupList) && !groupList.length) {
        const { data, groupId } = await getGroupMemberList(
          (selectedSession as any)?.info.gid,
          "group"
        );
        groupList = data || [];
      }
      if (!groupList.length) {
        toast.error("该组已解散");
        return;
      }
      let gid = owner.gid;
      let gname = owner.name;
      let description = owner.intro;
      let admins: any = [];
      for (let item of groupList) {
        if (item.identity === "administrator" || item.identity === "sub_administrator") {
          admins.push((item as any).info.userId);
        }
      }
      let result = await groupCreates({
        admins,
        folderId: gid,
        name: gname,
        description,
        parent: "groupRoot"
      });
      if (!result || result.errMsg) {
        toast.error("create_group_team_space_error");
      }
    } catch (e) {
      e && toast.error(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupRosters]);

  const handleWebFileOperate = async (operateKey: string) => {
    const isGroup = selectedSession.isGroup;
    if (operateKey === "cancel-download") {
      dXhr.abort?.();
      setStatus(FileStatus.CANCELED);
      setProgress(0);
    } else {
      const isExpired = checkFileIsExpired({
        expiredAppKey: imAppKey,
        message,
        updateSessionMessageMid: isMergeForward ? undefined : updateSessionMessageMid,
        expiredTime: im.fileExpiredTime
      });
      if (isExpired) {
        setIsFileExpired(true);
        return;
      }
      if (!FocusSDK.isPrivateNet() && message.type === ChatMessageType.EGOV_FILE) {
        toast.error(t("egov-file-notice"));
        return;
      }
      const name = getFileName(message);
      const url = await addXclient(
        getFileUrl(message.url, getFileDownloadBzOptions(selectedSession, currentEmployee))
      );
      dXhr = downLoadFile(name, url, "", {
        onProgress: (currPercent: any) => {
          setStatus(FileStatus.DOWNLOADING);
          setProgress(currPercent);
        },
        onFinish: () => {
          setStatus(FileStatus.COMPLETED);
          setProgress(100);
          dXhr = {};
          toast.success("下载成功");
          // handleSetReadTime();
          if (!isMergeForward) {
            handleAddFileDownloadRecord(message, currentEmployee, 0, selectedSession);
          }
        }
      });
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fileOperate = async (operateKey: string) => {
    const isGroup = selectedSession.isGroup;
    const _path = path
      ? path
      : `${defaultDownloadPath}${detectOS() === "Win" ? "\\" : "/"}${message.name}`;
    if (!operateKey) {
      return;
    }
    let isExpired;
    const netServer = NetworkCheck.getInstance({
      url: getNetCheckUrl(),
      checkType: UseCheckType.API
    });
    const onLine = await netServer.checkNet();
    if (!onLine && operateKey !== "cancel-download") {
      return toast.error(i18n.t("Network Error"));
    }
    if (operateKey === "click-to-wps-preview") {
      if (canWpsPreview) {
        isExpired = checkFileIsExpired({
          expiredAppKey: imAppKey,
          message,
          updateSessionMessageMid: isMergeForward ? undefined : updateSessionMessageMid,
          expiredTime: im.fileExpiredTime
        });
        if (isExpired) {
          setIsFileExpired(true);
          return;
        }
        if (isEgovFile) {
          toast.info(t("egov-file-preview"));
          return;
        }

        if (isWpsFile) {
          let previewParams: any = {
            url: message.url,
            mid: message.mid,
            groupId: selectedSession.isGroup ? selectedSession.sessionId : undefined,
            id: message.id
          };
          if (!isMergeForward) {
            previewParams = {
              ...previewParams,
              fileName: message.name,
              from: {
                pin: message.from.pin,
                app: message.from.app,
                teamId: message.from.teamId
              }
            };
            if (selectedSession.isSingle) {
              const toUser = parseMessageToUserBySingle(selectedSession.sessionId, {
                ...message?.from,
                userId: message?.from?.pin
              } as any);
              previewParams.to = toUser;
            }
          } else {
            previewParams.id = (message as any).muuid;
            previewParams = {
              ...previewParams,
              from: pick(message.from || {}, ["app", "pin", "teamId"])
            };
            if (selectedSession.isSingle) {
              previewParams.to =
                message?.to ||
                parseMessageToUserBySingle(selectedSession.sessionId, {
                  ...message?.from,
                  userId: message?.from?.pin
                } as any);
            }
          }
          getWpsPreviewCode(previewParams).then((res) => {
            if (res[0]) {
              if (!isMergeForward) {
                handleAddFileDownloadRecord(message, currentEmployee, 1, selectedSession);
              }
              openJoyspacePreview({
                host: joyspaceHost,
                code: res[0].code,
                isShowHeader: 1,
                canSaveToJoyspace: canJoyspaceEdit,
                gid: selectedSession.isGroup ? selectedSession.sessionId : undefined,
                gname: selectedSession.isGroup ? selectedSession.info.name : "",
                canEdit: canJoyspaceEdit && getPreviewCanEdit(previewParams.fileName),
                canForward: 1
              });
            } else {
              toast.error(res[1] || "网络异常");
            }
          });
        } else if (isImageFile) {
          getPreviewImageModal({
            message,
            isMergeForward,
            modalMessages: [
              {
                url: message.url,
                fileName: getMessageFileName(message),
                id: message.id,
                muuid: message.muuid,
                kind: "image"
              }
            ]
          });
        } else {
          toast.info("暂不支持预览此类型文件");
        }
      } else if (!hasAuthPreview) {
        toast.info(t("file-secret-tip"));
        return;
      }
      return;
    }

    if (!isFocusEnv() && operateKey !== "click-to-saveTo") {
      await handleWebFileOperate(operateKey);
    } else {
      switch (operateKey) {
        case "click-to-save":
          isExpired = checkFileIsExpired({
            expiredAppKey: imAppKey,
            message,
            updateSessionMessageMid: isMergeForward ? undefined : updateSessionMessageMid,
            expiredTime: im.fileExpiredTime
          });
          if (isExpired) {
            setIsFileExpired(true);
            return;
          }
          if (!FocusSDK.isPrivateNet() && message.type === ChatMessageType.EGOV_FILE) {
            toast.error(t("egov-file-notice"));
            return;
          }
          doDownloadMessageFile({
            viewFile: true,
            saveAs: imDownloadSaveAs,
            message,
            isGroup
          })
            .then(() => {
              // handleSetReadTime();
            })
            .catch((e) => console.log(e));
          break;
        case "re-download":
          isExpired = checkFileIsExpired({
            expiredAppKey: imAppKey,
            message,
            updateSessionMessageMid,
            expiredTime: im.fileExpiredTime
          });
          if (isExpired) {
            setIsFileExpired(true);
            return;
          }
          if (!FocusSDK.isPrivateNet() && message.type === ChatMessageType.EGOV_FILE) {
            toast.error(t("egov-file-notice"));
            return;
          }
          doDownloadMessageFile({
            viewFile: true,
            saveAs: imDownloadSaveAs,
            message,
            isGroup
          })
            .then(() => {
              // handleSetReadTime();
            })
            .catch((e) => console.log(e));
          break;
        case "click-to-saveAs":
          isExpired = checkFileIsExpired({
            expiredAppKey: imAppKey,
            message,
            updateSessionMessageMid,
            expiredTime: im.fileExpiredTime
          });
          if (isExpired) {
            setIsFileExpired(true);
            return;
          }
          if (!FocusSDK.isPrivateNet() && message.type === ChatMessageType.EGOV_FILE) {
            toast.error(t("egov-file-notice"));
            return;
          }
          doDownloadMessageFile({
            viewFile: false,
            saveAs: true,
            message,
            isGroup
          })
            .then(() => {
              // handleSetReadTime();
            })
            .catch((e) => console.log(e));
          break;
        case "download-resume":
          if (!FocusSDK.isPrivateNet() && message.type === ChatMessageType.EGOV_FILE) {
            toast.error(t("egov-file-notice"));
            return;
          }
          downloadItem?.resume();
          break;
        case "click-to-view":
          if (_path) {
            FocusSDK.checkLocalPathExist(_path, async (exists: boolean) => {
              if (exists) {
                FocusSDK.openItem(_path);
              } else {
                isExpired = checkFileIsExpired({
                  expiredAppKey: imAppKey,
                  message,
                  updateSessionMessageMid,
                  expiredTime: im.fileExpiredTime
                });
                if (isExpired) {
                  setIsFileExpired(true);
                  return;
                }
                if (!FocusSDK.isPrivateNet() && message.type === ChatMessageType.EGOV_FILE) {
                  toast.error(t("egov-file-notice"));
                  return;
                }
                setStatus(FileStatus.DOWNLOADING);
                setProgress(0);
                setTimeout(() => {
                  doDownloadMessageFile({
                    viewFile: true,
                    saveAs: false,
                    message,
                    isGroup
                  })
                    .then(() => {
                      // handleSetReadTime();
                    })
                    .catch((e) => console.log(e));
                }, 50);
              }
            });
          }
          break;
        case "open-folder":
          if (_path) {
            FocusSDK.checkLocalPathExist(_path, async (exists: boolean) => {
              if (exists) {
                FocusSDK.showItemInFolder(_path);
              } else {
                isExpired = checkFileIsExpired({
                  expiredAppKey: imAppKey,
                  message,
                  updateSessionMessageMid,
                  expiredTime: im.fileExpiredTime
                });
                if (isExpired) {
                  setIsFileExpired(true);
                  return;
                }
                if (!FocusSDK.isPrivateNet() && message.type === ChatMessageType.EGOV_FILE) {
                  toast.error(t("egov-file-notice"));
                  return;
                }
                // setStatus(FileStatus.DOWNLOADING);
                // setProgress(0);
                setTimeout(() => {
                  doDownloadMessageFile({
                    viewFile: true,
                    saveAs: true,
                    message,
                    isGroup
                  })
                    .then()
                    .catch((e) => console.log(e));
                }, 50);
              }
            });
          }
          break;
        case "cancel-download":
          downloadItem?.pause();
          break;
        case "click-to-saveTo":
          isExpired = checkFileIsExpired({
            expiredAppKey: imAppKey,
            message,
            updateSessionMessageMid,
            expiredTime: im.fileExpiredTime
          });
          if (isExpired) {
            setIsFileExpired(true);
            return;
          }
          handleTransferTo(
            message,
            selectedSession,
            currentEmployee,
            groupCreate,
            appRuntimeEnv,
            userData,
            updateSessionMessageMid
          );
          break;
        default:
          break;
      }
    }
  };

  const onDragStart = (e: any) => {
    if (!isFocusEnv()) {
      return;
    }
    if (!FocusSDK.isPrivateNet() && message.type === ChatMessageType.EGOV_FILE) {
      toast.error(t("egov-file-notice"));
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    // console.log('download', 'message', message, 'path', path, 'isDragging', isDragging);
    if (connectState && connectState.state === ConnectState.READY) {
      if (isDragging) {
        toast.warning(t("file-downloading-drag-save"));
        return;
      }
      if (!path) {
        setIsDragging(true);
        toast.warning(t("file-downloading-drag-save"));

        doDownloadMessageFile({ viewFile: false, saveAs: false, message }).then();
      } else {
        FocusSDK.checkLocalPathExist(path, (exists: boolean) => {
          if (exists) {
            FocusSDK.startDrag(path);
          } else {
            setStatus(FileStatus.DOWNLOADING);
            setProgress(0);
            setTimeout(() => {
              if (isDragging) {
                toast.warning(t("file-downloading-drag-save"));
                return;
              }
              setIsDragging(true);
              toast.warning(t("file-downloading-drag-save"));

              doDownloadMessageFile({
                viewFile: false,
                saveAs: false,
                message
              })
                .then()
                .catch((e) => console.log(e));
            }, 50);
          }
        });
      }
    } else {
      toast.warning(t("drag-after-the-network-is-normal"));
      return;
    }
  };

  const OpenDropdownContent = useMemo(() => {
    return (
      <div className="file-dropdown-content">
        <div className="file-dropdown-item" onClick={() => fileOperate("click-to-view")}>
          {t("open-file")}
        </div>
        <div className="file-dropdown-item" onClick={() => fileOperate("open-folder")}>
          {t("open-folder")}
        </div>
      </div>
    );
  }, [t, fileOperate]);

  const handleClickRecentlyFile = useCallback(() => {
    const transferUrl = `${joyspaceHost}/transferResult?transferCode=${message.docId}`;
    window.open(transferUrl);
  }, [joyspaceHost, message.docId]);

  const isIntent = useMemo(() => {
    return showIntentTail(message);
  }, [message]);

  const SaveToDropdownContent = useMemo(() => {
    return (
      <div className="file-dropdown-content">
        <div className="file-dropdown-item" onClick={handleClickRecentlyFile}>
          {t("open-recently-saveTo-file")}
        </div>
        <div className="file-dropdown-item" onClick={() => fileOperate("click-to-saveTo")}>
          {t("saveTo-again")}
        </div>
      </div>
    );
  }, [handleClickRecentlyFile, t, fileOperate]);

  const [fileIcon] = getFileIconByMime(lookup(message.name) || "");
  const isDrag =
    FocusSDK.getDeviceInfo().platform !== "web" && !isMergeForward && !selectedSession?.isSecret;

  // 文件tag名称
  const fileTagName = useCallback(
    (allName = true) => {
      return getFileTagName({
        allName,
        fileTag: message?.fileTag,
        fileTagList,
        isEgovFile
      });
    },
    [message?.fileTag, fileTagList, isEgovFile]
  );

  const showFileTag = useMemo(() => {
    return isEgovFile || (message?.fileTag || 0) !== 0;
  }, [isEgovFile, message.fileTag]);

  const fileTagBottomNameComp = useMemo(() => {
    if (!showFileTag || (fileTagList?.length || 0) <= 1) {
      return null;
    }
    return FileTipTag(fileTagName(), message?.fileTag || 0);
  }, [fileTagName, message.fileTag, showFileTag]);

  return (
    <>
      <div
        className="content file"
        draggable={isDrag}
        onDragStart={onDragStart}
        style={{ width: "336px" }}
      >
        <div
          className="file-content"
          onClick={() => {
            fileOperate("click-to-wps-preview");
          }}
          clstag={fileOperateClsTag(status === FileStatus.COMPLETED ? operateKey2 : operateKey1)}
        >
          <div className="file-content-name" style={isSmall ? { width: "100%", padding: "0" } : {}}>
            {message.name}
          </div>
          <div
            className="file-content-item"
            style={{
              width: "100%",
              position: "absolute",
              bottom: "-2px",
              alignItems: "center",
              padding: "0"
            }}
          >
            <div className="file-info-status-size">
              <span>{sizeToStr(message.size)}</span>
              {isWpsFile && canWpsPreview && !isEgovFile && (
                <span className="download-file-view-text">点击在线预览</span>
              )}
            </div>

            {status === FileStatus.FAILED ||
              (status === FileStatus.FAILEDRETRY && (
                <div className="file-info-dow">
                  {/* 下载失败 */}
                  <span className="file-sucess">
                    {t("download-error")}
                    <IconFont type="icona-ic_pc_im_reupload" className="file-icon-warning" />
                  </span>
                </div>
              ))}
            {status === FileStatus.COMPLETED && (
              <div className="file-info-dow">
                {/* 下载成功 */}
                <span className="file-sucess">
                  {t("download-sucess")}
                  <IconFont type="iconjs_ic_success" className="file-icon-sucess" />
                </span>
              </div>
            )}
            {status === FileStatus.DOWNLOADING && !fileExpired && (
              <div className="file-info-dow">
                <span>{t("file-downloading")}</span>
              </div>
            )}
          </div>
        </div>

        <div className="file-icon-container">
          <img
            src={changeEgovUrl(fileIcon)}
            alt=""
            className="file-icon"
            draggable="false"
            onClick={() => {
              fileOperate("click-to-wps-preview");
            }}
          />
          {/* {(message.type === ChatMessageType.EGOV_FILE ||
            message.kind === ChatMessageType.EGOV_FILE) && (
            <img src={changeEgovUrl(egovFileIcon)} className="egov-file-icon" alt="" />
          )} */}
        </div>
        {status === FileStatus.DOWNLOADING && !fileExpired && (
          <div
            className="file-img-mask"
            style={{
              backgroundImage: `conic-gradient(transparent ${progress * 3.6
                }deg,  rgba(0, 0, 0, 0.3) ${progress * 3.6}deg)`
            }}
          />
        )}
        {/* 文件操作按钮 */}
        {isOperateNoAuth ? (
          <div className="file-status-bom">
            <span className="file-status-bom-text expired">{t("file-no-operation")}</span>
          </div>
        ) : fileExpired ? (
          <div className="file-status-bom">
            <span className="file-status-bom-text expired">{t("file-expired")}</span>
          </div>
        ) : isFocusEnv() ? (
          <div className="file-status-bom" style={{ justifyContent: "space-between" }}>
            <div className="btn-left">
              {isWpsFile && canWpsPreview && !isEgovFile && (
                <span
                  className="file-status-bom-text"
                  onClick={() => {
                    fileOperate("click-to-wps-preview");
                  }}
                >
                  {t("wps-preview")}
                </span>
              )}
            </div>
            <div className="btn-right">
              {!selectedSession.isSecret &&
                isDisplay &&
                !isEgovFile &&
                (message.docId ? (
                  <Dropdown
                    overlay={SaveToDropdownContent}
                    placement="bottomCenter" /* trigger={['click']} */
                  >
                    <span
                      className="file-status-bom-text transferTo"
                      style={{ marginRight: "40px" }}
                    >
                      <span className="bom-text">{t("already-saveTo")}</span>
                      <DownOutlined />
                    </span>
                  </Dropdown>
                ) : isMeetingChat ? null : (
                  <span
                    className="file-status-bom-text transferTo"
                    style={{ marginRight: "40px" }}
                    onClick={() => fileOperate(operateKey3)}
                  >
                    {t(operateKey3)}
                  </span>
                ))}
              {/* <span
            className="file-status-bom-text"
            style={{ marginRight: "40px" }}
            onClick={() => fileOperate(operateKey2)}
          >
            {t(operateKey2)}
          </span> */}
              {status === FileStatus.COMPLETED ? (
                <Dropdown
                  overlay={OpenDropdownContent}
                  placement="bottomCenter" /* trigger={['click']} */
                >
                  <span className="file-status-bom-text">
                    <span className="bom-text">{t(operateKey1)}</span>
                    <DownOutlined />
                  </span>
                </Dropdown>
              ) : (
                <span className="file-status-bom-text" onClick={() => fileOperate(operateKey1)}>
                  {t(operateKey1)}
                </span>
              )}
            </div>
          </div>
        ) : (
          <div className="file-status-bom" style={{ justifyContent: "space-between" }}>
            <div className="btn-left">
              {isWpsFile && canWpsPreview && !isEgovFile && (
                <span
                  className="file-status-bom-text"
                  onClick={() => {
                    fileOperate("click-to-wps-preview");
                  }}
                >
                  {t("wps-preview")}
                </span>
              )}
            </div>
            <div className="btn-right">
              {!selectedSession.isSecret &&
                isDisplay &&
                !isEgovFile &&
                (message.docId ? (
                  <Dropdown
                    overlay={SaveToDropdownContent}
                    placement="bottomCenter" /* trigger={['click']} */
                  >
                    <span
                      className="file-status-bom-text transferTo"
                      style={{ marginRight: "40px" }}
                    >
                      <span className="bom-text">{t("already-saveTo")}</span>
                      <DownOutlined />
                    </span>
                  </Dropdown>
                ) : isMeetingChat ? null : (
                  <span
                    className="file-status-bom-text transferTo"
                    style={{ marginRight: "40px" }}
                    onClick={() => fileOperate(operateKey3)}
                  >
                    {t(operateKey3)}
                  </span>
                ))}
              <span className="file-status-bom-text" onClick={() => fileOperate(operateKey1)}>
                {t(operateKey1)}
              </span>
            </div>
          </div>
        )}
      </div>
      <div className="file-ai-tail">
        {isIntent ? <AIIntentTail message={message} clsName="stick" /> : null}
      </div>
      <div className={`file-extr ${showFileTag || isIntent ? "multi" : ""}`}>
        
        {fileTagBottomNameComp}
        {isEqualEmployee(message.sender, currentEmployee) &&
          selectedSession.isGroup &&
          (selectedSession.info as any).msgDongRead === 1 && (
            <MessageDongReadStatusBtn {...props} />
          )}
        {isEqualEmployee(message.sender, currentEmployee) && !selectedSession.isSecret && (
          <div className="upload-detail-btn-box">
            <UploadWithDetailBtn {...props} />
          </div>
        )}
      </div>
    </>
  );
}

function mapStateToProps({ chat, user: { userData } }: any) {
  const {
    connectState,
    selectedSession,
    currentEmployee,
    downloadingMids,
    saveAsCount
  } = chat as ChatState;
  return {
    connectState,
    selectedSession,
    currentEmployee,
    downloadingMids,
    saveAsCount,
    userData: userData ? userData.user : {}
  };
}

function mapDispatchToProps(dispatch: any) {
  return {
    updateSessionMessageMid(data: { sendResult: any; sessionId: String }) {
      return dispatch({ type: "chat/updateSessionMessageMid", payload: data });
    },
    updateSessionMessagesTimestamp(data: { timestamp: number }) {
      dispatch({ type: "chat/updateSessionMessagesTimestamp", payload: data });
    },
    updateDownloadingMids(data: string[]) {
      dispatch({ type: "chat/updateDownloadingMids", payload: data });
    },
    pushChatMessageMid(data: { sessionId: string; message: Partial<ChatMessage> }) {
      dispatch({ type: "chat/pushChatMessageMid", payload: data });
    }
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(DownLoadFile);
